/**
 * **O fundo do mundo aberto: estrelas, e mais nada.**
 *
 * ## Porque não é um mosaico
 *
 * Um fundo feito de uma imagem repetida denuncia-se sozinho: o olho encontra o
 * quadrado em poucos segundos e a partir daí não vê outra coisa. Um quadrante
 * tem 81 600 unidades de lado e uma expedição atravessa-o em quatro minutos —
 * tempo mais do que suficiente para contar as repetições.
 *
 * ## Como isto não repete, e não tem costura
 *
 * O plano é dividido em **células** e cada célula é semeada pelo seu próprio par
 * de coordenadas. Duas células diferentes produzem estrelas diferentes porque a
 * semente é diferente; células vizinhas encaixam sem costura porque não há nada
 * a encaixar — cada uma preenche exatamente o seu quadrado e nenhuma sabe da
 * outra.
 *
 * É determinístico: a mesma célula produz sempre as mesmas estrelas, portanto
 * voar para leste e voltar mostra o mesmo céu. Não há memória e não há estado —
 * o céu é uma função da posição.
 *
 * ## O paralaxe
 *
 * Três camadas, cada uma amostrada num referencial que se move a uma fração da
 * câmara. Uma camada distante desloca-se pouco e por isso parece longe. O
 * mesmo esquema de células vale em cada uma, portanto nenhuma delas tem costura.
 *
 * Discreto de propósito: um paralaxe forte transforma o fundo no que se vê, e o
 * que se tem de ver são naves e tiros.
 *
 * ## A cintilação
 *
 * Cada estrela respira. **Não pisca**: a opacidade e a escala percorrem um seno
 * lento, entre dois segundos e meio e cerca de oito, e voltam. O que faz isto
 * parecer vivo em vez de mecânico é que a **fase e o período de cada estrela
 * saem da própria célula**, pela mesma dobra de bits que decidiu onde ela está:
 * duas estrelas vizinhas têm ritmos diferentes e nunca se apanham em fase, e o
 * campo inteiro nunca respira em uníssono.
 *
 * Determinístico como as posições. `nowMs` é o relógio da cena e entra na conta,
 * portanto o brilho ANDA — mas onde ele está em cada instante é uma função pura
 * de `(célula, índice, agora)`, sem estado guardado por estrela e sem uma
 * segunda passagem pelo gerador. Voar para leste e voltar mostra o mesmo céu, a
 * respirar da mesma maneira.
 *
 * ## Nada é alocado num quadro
 *
 * Nem um objeto, nem um array, nem uma closure, nem uma textura. `update`
 * recebe cinco números soltos e não um literal — um literal de câmara por
 * quadro seria um objeto por quadro — e por dentro só escreve em sprites que já
 * existem. Não há um `tween` nem um emissor por estrela: seriam trezentos
 * temporizadores para desenhar um seno que se calcula em duas multiplicações.
 *
 * ## O que nunca há aqui
 *
 * Nebulosas, nuvens, PNGs temporários e texturas por cor. As texturas são as
 * três brancas que o jogo já tem, coloridas por `setTint` — uma textura por cor
 * partiria o batching do Phaser, que é uma coisa que já custou metade da taxa de
 * frames uma vez.
 */

import Phaser from 'phaser';
import type { DashTextureScene } from './projectile-art';
import { ensureStarCrossTexture, ensureStarGlowTexture, STAR_TINTS } from './starfield';

/**
 * O lado de uma célula, em unidades de mundo.
 *
 * Grande o suficiente para uma câmara ver poucas de cada vez — o custo é
 * proporcional ao número de células visíveis — e pequeno o suficiente para a
 * densidade parecer uniforme em vez de aos aglomerados.
 */
const CELL_SIZE = 900;

/** Quantas estrelas por célula, por camada. Poucas, e é a densidade. */
const STARS_PER_CELL = 7;

/**
 * As três camadas: a fração da câmara a que cada uma se move, e o tamanho.
 *
 * A mais distante quase não se mexe e é quase invisível; a mais próxima
 * acompanha o movimento sem o igualar. Nenhuma chega perto de `1`, porque uma
 * camada que acompanhasse a câmara por inteiro seria papel de parede colado.
 */
const LAYERS = [
  { parallax: 0.12, scale: 0.16, alpha: 0.35, cross: false },
  { parallax: 0.26, scale: 0.24, alpha: 0.55, cross: false },
  { parallax: 0.45, scale: 0.4, alpha: 0.8, cross: true },
] as const;

/**
 * O baralhador. **Um inteiro para dentro, um inteiro para fora.**
 *
 * Uma dobra de bits determinística, exata em qualquer motor: o mesmo par de
 * coordenadas dá o mesmo céu em Node, em workerd e em qualquer browser. Um
 * `Math.random` semeado seria uma sequência, e uma sequência precisa de ordem —
 * o que obrigaria a desenhar as células sempre pela mesma ordem para ver o mesmo
 * céu.
 */
function hash(x: number, y: number, salt: number): number {
  let value = (x * 0x1f1f_1f1f) ^ (y * 0x8da6_b343) ^ (salt * 0xd8163841);
  value = Math.imul(value ^ (value >>> 16), 0x21f0_aaad);
  value = Math.imul(value ^ (value >>> 15), 0x735a_2d97);
  return (value ^ (value >>> 15)) >>> 0;
}

/** O mesmo, normalizado para `[0, 1)`. */
function unit(x: number, y: number, salt: number): number {
  return hash(x, y, salt) / 0x1_0000_0000;
}

/**
 * Os limites do ritmo, em milissegundos.
 *
 * O mais rápido é dois segundos e meio, que é lento o suficiente para se ler
 * como respiração e não como um `flicker`; o mais lento é quase oito, que é
 * longo o suficiente para o céu nunca parecer sincronizado.
 */
const TWINKLE_MIN_MS = 2500;
const TWINKLE_SPAN_MS = 5200;

/** Duas voltas de `sin` por período. Constante, e não uma multiplicação por 6.28. */
const TAU = Math.PI * 2;

interface StarSprite {
  readonly sprite: Phaser.GameObjects.Image;
  /** Qual camada o desenha. Decide o paralaxe e o tamanho. */
  readonly layer: number;
}

/**
 * O céu de um quadrante, desenhado a partir da posição da câmara.
 *
 * Os sprites são um conjunto **fixo e reciclado**: nada é criado nem destruído
 * durante um frame. Quando a câmara anda, os mesmos objetos passam a representar
 * as estrelas das células que entraram — o que é o que faz um céu infinito
 * custar um número constante de objetos.
 */
export class OpenWorldStarfield {
  private readonly sprites: StarSprite[] = [];
  private readonly glowKey: string;
  private readonly crossKey: string;

  constructor(
    private readonly scene: Phaser.Scene & DashTextureScene,
    private readonly depth: number,
  ) {
    this.glowKey = ensureStarGlowTexture(scene);
    this.crossKey = ensureStarCrossTexture(scene);
  }

  /**
   * Redesenha o céu para esta câmara.
   *
   * Percorre as células visíveis de cada camada, deriva as estrelas de cada uma
   * e atribui-as aos sprites do conjunto. Um sprite a mais fica escondido; se
   * faltarem, são criados uma vez e reutilizados a partir daí.
   */
  update(
    cameraX: number,
    cameraY: number,
    viewWidth: number,
    viewHeight: number,
    nowMs: number,
  ): void {
    let used = 0;
    for (let index = 0; index < LAYERS.length; index += 1) {
      const layer = LAYERS[index];
      if (layer === undefined) {
        continue;
      }
      /*
       * O referencial desta camada. A câmara move-se `parallax` vezes menos
       * aqui, e é isso — e só isso — que faz uma camada parecer distante.
       */
      const originX = cameraX * layer.parallax;
      const originY = cameraY * layer.parallax;
      const firstX = Math.floor(originX / CELL_SIZE) - 1;
      const firstY = Math.floor(originY / CELL_SIZE) - 1;
      const lastX = Math.floor((originX + viewWidth) / CELL_SIZE) + 1;
      const lastY = Math.floor((originY + viewHeight) / CELL_SIZE) + 1;

      for (let cellY = firstY; cellY <= lastY; cellY += 1) {
        for (let cellX = firstX; cellX <= lastX; cellX += 1) {
          for (let star = 0; star < STARS_PER_CELL; star += 1) {
            const salt = index * 97 + star * 7919;
            /*
             * A posição DENTRO da célula, derivada da célula. Duas células
             * diferentes dão estrelas diferentes; a mesma célula dá sempre as
             * mesmas. Não há costura porque não há nada a encaixar.
             */
            const localX = unit(cellX, cellY, salt) * CELL_SIZE;
            const localY = unit(cellX, cellY, salt + 1) * CELL_SIZE;
            const worldX = cellX * CELL_SIZE + localX - originX + cameraX;
            const worldY = cellY * CELL_SIZE + localY - originY + cameraY;

            const entry = this.spriteAt(used, index);
            used += 1;
            const sprite = entry.sprite;
            const roll = unit(cellX, cellY, salt + 2);
            const big = layer.cross && roll > 0.94;
            sprite.setTexture(big ? this.crossKey : this.glowKey);
            sprite.setPosition(worldX, worldY);
            /*
             * **A respiração desta estrela.** Fase e período saem da célula,
             * portanto são sempre os mesmos para ela e diferentes da vizinha.
             */
            const phase = unit(cellX, cellY, salt + 5);
            const period = TWINKLE_MIN_MS + unit(cellX, cellY, salt + 6) * TWINKLE_SPAN_MS;
            /* De `-1..1` para `0..1`: onde esta estrela está no seu ciclo. */
            const wave = 0.5 + 0.5 * Math.sin((nowMs / period + phase) * TAU);
            /*
             * Quanto ela varia. Umas quase não respiram e outras apagam-se a
             * meio — é a mistura que faz um céu, e não trezentas cópias.
             */
            const depth = 0.25 + unit(cellX, cellY, salt + 7) * 0.45;

            const size = layer.scale * (0.6 + roll * 0.8);
            /* A escala varia pouco: o que se lê é o brilho, e não o tamanho. */
            sprite.setScale((big ? size * 1.6 : size) * (0.88 + 0.12 * wave));
            sprite.setTint(
              STAR_TINTS[hash(cellX, cellY, salt + 3) % STAR_TINTS.length] ?? 0xffffff,
            );
            sprite.setAlpha(
              layer.alpha *
                (0.55 + unit(cellX, cellY, salt + 4) * 0.45) *
                (1 - depth + depth * wave),
            );
            sprite.setVisible(true);
          }
        }
      }
    }
    for (let index = used; index < this.sprites.length; index += 1) {
      this.sprites[index]?.sprite.setVisible(false);
    }
  }

  destroy(): void {
    for (const entry of this.sprites) {
      entry.sprite.destroy();
    }
    this.sprites.length = 0;
  }

  /**
   * O sprite nesta posição do conjunto, criado uma vez se ainda não existir.
   *
   * O conjunto cresce até ao que uma câmara vê e depois nunca mais: os frames
   * seguintes reutilizam exatamente os mesmos objetos, portanto não há alocação
   * durante um frame.
   */
  private spriteAt(index: number, layer: number): StarSprite {
    const existing = this.sprites[index];
    if (existing !== undefined) {
      return existing;
    }
    const sprite = this.scene.add.image(0, 0, this.glowKey);
    sprite.setDepth(this.depth);
    sprite.setBlendMode(Phaser.BlendModes.ADD);
    sprite.setScrollFactor(1);
    const entry: StarSprite = { sprite, layer };
    this.sprites.push(entry);
    return entry;
  }
}
